import { useEffect } from 'react';

import { type QueryClient, useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { useAuthStore } from 'lib/stores/authStore';
import type { NonwithdrawableCredit } from 'models/portfolio';
import { setSprigAttribute, setSprigAttributeAndSoftReload } from 'utils/sprig';
import { SPRIG_HAS_PERPS_BALANCE_ATTRIBUTE_NAME } from 'utils/sprig-constants';
import { getSessionStorageItem, setSessionStorageItem } from 'utils/storage';
import { sleep } from 'utils/time';

import { exchangeApi } from './api';
import { type MarginPollingOptions, MARGIN_POLL_INTERVAL_MS, resolveMarginPollInterval } from './marginPolling';
import { useMarginAccountEnabled } from './useMarginAccountEnabled';

export interface SubaccountBalance {
    account_equity: string;
    available_balance: string;
    initial_margin: string;
    maintenance_margin: string;
    position_value: string;
    resting_orders_margin: string;
    subaccount: number;
    unrealized_pnl: string;
}

export interface MarginBalance {
    account_equity: string;
    net_deposits: string;
    initial_margin: string;
    maintenance_margin: string;
    position_value: string;
    resting_orders_margin: string;
    settled_funds: string;
    subaccount_balances: SubaccountBalance[];
    unrealized_pnl: string;
    withdrawable: string;
    // Non-withdrawable promotional credit balance in dollars (omitted when zero or until backend ships)
    nonwithdrawable_amount?: string;
    // Per-credit breakdown of the non-withdrawable balance (omitted until backend ships)
    nonwithdrawable_credits?: NonwithdrawableCredit[];
}

export const marginBalanceKeys = {
    all: ['marginBalance'] as const,
    user: (userId: string) => [...marginBalanceKeys.all, userId] as const,
};

export const getMarginBalance = async (userId: string, signal?: AbortSignal): Promise<MarginBalance> => {
    const response = await exchangeApi.get<MarginBalance>(`/v1/users/${userId}/margin/balance`, { signal });
    return response.data;
};

export function createZeroMarginBalance(): MarginBalance {
    return {
        account_equity: '0',
        net_deposits: '0',
        initial_margin: '0',
        maintenance_margin: '0',
        position_value: '0',
        resting_orders_margin: '0',
        settled_funds: '0',
        subaccount_balances: [
            {
                subaccount: 0,
                account_equity: '0',
                available_balance: '0',
                initial_margin: '0',
                maintenance_margin: '0',
                position_value: '0',
                resting_orders_margin: '0',
                unrealized_pnl: '0',
            },
        ],
        unrealized_pnl: '0',
        withdrawable: '0',
    };
}

export function getPrimarySubaccountAvailableBalance(balance: MarginBalance | undefined): string | undefined {
    return balance?.subaccount_balances?.find((subaccount) => subaccount.subaccount === 0)?.available_balance;
}

/** Non-withdrawable credit balance in dollars; falls back to summing active credits. */
export function getNonwithdrawableDollars(balance: MarginBalance | undefined): number {
    if (balance?.nonwithdrawable_amount != null) {
        try {
            return new Decimal(balance.nonwithdrawable_amount).toNumber();
        } catch {
            return 0;
        }
    }
    if (balance?.nonwithdrawable_credits) {
        return (
            balance.nonwithdrawable_credits
                .filter((credit) => credit.status === 'active')
                .reduce((sum, credit) => sum + credit.amount, 0) / 100
        );
    }
    return 0;
}

export interface MarginTransferBreakdown {
    availableToTransfer: Decimal;
    pendingSettlement: Decimal;
}

function getPendingSettlementDollars(balance: MarginBalance): Decimal {
    try {
        return Decimal.max(
            0,
            new Decimal(balance.account_equity)
                .minus(balance.initial_margin)
                .minus(balance.resting_orders_margin)
                .minus(balance.withdrawable),
        );
    } catch {
        return new Decimal(0);
    }
}

/**
 * Mirrors the backend's validation of
 * POST /trade-api/v2/portfolio/intra_exchange_instance_transfer for the
 * margined source: transfers are capped at `withdrawable` (the lesser of the
 * free balance and settled funds, computed server-side on GET /margin/balance)
 * minus non-withdrawable credits, which must stay on the margined rail.
 *
 * `availableToTransfer` reads the backend-owned `withdrawable` field directly.
 * There is no backend `pending_settlement` field, so `pendingSettlement` is
 * derived from the top-level margin balance fields as:
 * `max(account_equity - initial_margin - resting_orders_margin - withdrawable, 0)`.
 * Malformed values are treated as zero so the tooltip never shows an invalid
 * inferred amount.
 */
export function getMarginTransferBreakdown(balance: MarginBalance | undefined): MarginTransferBreakdown | undefined {
    if (!balance) return undefined;
    try {
        const withdrawable = new Decimal(balance.withdrawable);
        return {
            availableToTransfer: Decimal.max(0, withdrawable.minus(getNonwithdrawableDollars(balance))),
            pendingSettlement: getPendingSettlementDollars(balance),
        };
    } catch {
        return undefined;
    }
}

/**
 * Imperatively fetch subaccount 0's available balance (dollars) outside a React
 * render — `fetchQuery` with `staleTime: 0` hits the network, dedupes against
 * any in-flight background poll, and writes through the cache so mounted
 * observers (e.g. `MarginDataSync`) pick up the value. Returns undefined on a
 * transient fetch error so callers can retry.
 */
export async function fetchPrimarySubaccountAvailableBalance(
    queryClient: QueryClient,
    userId: string,
): Promise<string | undefined> {
    try {
        const balance = await queryClient.fetchQuery({
            queryKey: marginBalanceKeys.user(userId),
            queryFn: ({ signal }) => getMarginBalance(userId, signal),
            staleTime: 0,
        });
        return getPrimarySubaccountAvailableBalance(balance);
    } catch {
        return undefined;
    }
}

/**
 * Polls the margin balance until subaccount 0 can cover `targetAvailableDollars`
 * or the deadline passes (intra-exchange transfers settle asynchronously).
 */
export async function pollMarginBalanceUntilAvailable(
    queryClient: QueryClient,
    userId: string,
    targetAvailableDollars: Decimal,
    { timeoutMs = 10_000, intervalMs = MARGIN_POLL_INTERVAL_MS }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<{ funded: boolean; availableDollars: string | undefined }> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
        const available = await fetchPrimarySubaccountAvailableBalance(queryClient, userId);
        if (available != null && new Decimal(available).gte(targetAvailableDollars)) {
            return { funded: true, availableDollars: available };
        }
        if (Date.now() >= deadline) return { funded: false, availableDollars: available };
        await sleep(intervalMs);
    }
}

export function hasPositivePerpsBalance(marginBalance?: MarginBalance): boolean {
    if (!marginBalance?.account_equity) return false;

    try {
        return new Decimal(marginBalance.account_equity).greaterThan(0);
    } catch {
        return false;
    }
}

const sprigPerpsBalanceSoftReloadCompletedUserIds = new Set<string>();
const sprigPerpsBalanceSoftReloadPendingUserIds = new Set<string>();

function getSprigPerpsBalanceSoftReloadSessionKey(userId: string): string {
    return `sprigSoftReloadedAfterPerpsBalance:${userId}`;
}

function hasCompletedSprigSoftReloadForPerpsBalance(userId: string): boolean {
    return (
        sprigPerpsBalanceSoftReloadCompletedUserIds.has(userId) ||
        getSessionStorageItem(getSprigPerpsBalanceSoftReloadSessionKey(userId)) === 'true'
    );
}

function shouldStartSprigSoftReloadForPerpsBalance(userId: string): boolean {
    if (sprigPerpsBalanceSoftReloadPendingUserIds.has(userId) || hasCompletedSprigSoftReloadForPerpsBalance(userId)) {
        return false;
    }

    sprigPerpsBalanceSoftReloadPendingUserIds.add(userId);
    return true;
}

function clearPendingSprigSoftReloadForPerpsBalance(userId: string): void {
    sprigPerpsBalanceSoftReloadPendingUserIds.delete(userId);
}

function completeSprigSoftReloadForPerpsBalance(userId: string): void {
    clearPendingSprigSoftReloadForPerpsBalance(userId);
    sprigPerpsBalanceSoftReloadCompletedUserIds.add(userId);
    setSessionStorageItem(getSprigPerpsBalanceSoftReloadSessionKey(userId), 'true');
}

export const useMarginBalance = (options?: MarginPollingOptions) => {
    const userId = useAuthStore((s) => s.userId);
    const marginAccountEnabled = useMarginAccountEnabled();
    const queryEnabled = marginAccountEnabled && !!userId && (options?.enabled ?? true);
    const query = useQuery({
        queryKey: marginBalanceKeys.user(userId!),
        queryFn: ({ signal }) => getMarginBalance(userId!, signal),
        refetchInterval: resolveMarginPollInterval(options),
        enabled: queryEnabled,
    });

    const hasPerpsBalance = hasPositivePerpsBalance(query.data);
    useEffect(() => {
        if (!userId || !query.isSuccess) return;
        if (!hasPerpsBalance) {
            return setSprigAttribute(SPRIG_HAS_PERPS_BALANCE_ATTRIBUTE_NAME, false);
        }
        if (hasCompletedSprigSoftReloadForPerpsBalance(userId)) {
            return setSprigAttribute(SPRIG_HAS_PERPS_BALANCE_ATTRIBUTE_NAME, true);
        }
        if (!shouldStartSprigSoftReloadForPerpsBalance(userId)) {
            return () => {};
        }

        let softReloadQueued = false;
        const clearPendingIfIncomplete = () => {
            if (softReloadQueued) return;
            clearPendingSprigSoftReloadForPerpsBalance(userId);
        };
        const cleanupSprig = setSprigAttributeAndSoftReload(SPRIG_HAS_PERPS_BALANCE_ATTRIBUTE_NAME, true, {
            onSoftReloadFailed: clearPendingIfIncomplete,
            onSoftReloadQueued: () => {
                softReloadQueued = true;
                completeSprigSoftReloadForPerpsBalance(userId);
            },
        });

        return () => {
            cleanupSprig();
            clearPendingIfIncomplete();
        };
    }, [hasPerpsBalance, query.isSuccess, userId]);

    return query;
};
